import { useEffect, useMemo, useState } from "react";
import { Dialog, Flex, Text, Button, Spinner, Select, Separator, Card, TextField } from "@radix-ui/themes";
import { Cross2Icon, DotFilledIcon } from "@radix-ui/react-icons";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import params from "configs/params";
import { useActivateSubscription, useConfirmSubscription, useTaxPreview } from "hooks/billing/useBilling";
import { useToast } from "../../context/ToastContext";

countries.registerLocale(enLocale);

const stripePromise = loadStripe(params.stripePublishableKey);

const formatCurrency = (amount, currency = "gbp") => {
  const currencySymbols = {
    gbp: "£",
    usd: "$",
    eur: "€",
  };

  const parsedAmount = Number.parseFloat(amount ?? 0);
  const symbol = currencySymbols[currency.toLowerCase()] || currency.toUpperCase();
  return `${symbol}${Number.isNaN(parsedAmount) ? "0.00" : parsedAmount.toFixed(2)}`;
};

const getProductTotal = (orgProduct) => Number.parseFloat(orgProduct.product.price) * orgProduct.quantity;

const isMeteredUsageProduct = (orgProduct) => orgProduct?.product?.product_type === "metered_usage";

const getProductPriceLabel = (orgProduct) => {
  if (isMeteredUsageProduct(orgProduct)) {
    return "Usage-based";
  }

  return `${formatCurrency(getProductTotal(orgProduct), orgProduct.product.currency)}${
    orgProduct.product.billing_interval === "one_time"
      ? " (one-time)"
      : `/${orgProduct.product.billing_interval}`
  }`;
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

function OrderSummary({ products, taxPreview, isTaxLoading, taxError }) {
  return (
    <Flex direction="column" gap="3">
      <Text size="3" weight="bold">Order Summary</Text>

      {products?.map((orgProduct) => (
        <Flex key={orgProduct.id} justify="between" align="center">
          <Flex gap="1" align="center" style={{ alignSelf: "stretch" }}>
            <DotFilledIcon width={14} height={14} />
            <Text size="2" weight="medium">
              {orgProduct.product.product_name}
              {orgProduct.quantity > 1 && ` (x${orgProduct.quantity})`}
            </Text>
          </Flex>
          <Text size="2" weight="bold">
            {getProductPriceLabel(orgProduct)}
          </Text>
        </Flex>
      ))}

      {isTaxLoading && (
        <Flex align="center" gap="2" p="2">
          <Spinner size="1" />
          <Text size="2" color="gray">Calculating tax...</Text>
        </Flex>
      )}

      {taxPreview && !isTaxLoading && (
        <Flex direction="column" gap="2" mt="2">
          <Separator size="4" />
          <Flex justify="between" align="center">
            <Text size="2" color="gray">Subtotal</Text>
            <Text size="2">{formatCurrency(taxPreview.subtotal ?? 0, taxPreview.currency || "gbp")}</Text>
          </Flex>
          {taxPreview.tax > 0 && (
            <Flex justify="between" align="center">
              <Text size="2" color="gray">VAT ({(taxPreview.tax_rate ?? 0).toFixed(0)}%)</Text>
              <Text size="2">{formatCurrency(taxPreview.tax ?? 0, taxPreview.currency || "gbp")}</Text>
            </Flex>
          )}
          <Separator size="4" />
          <Flex justify="between" align="center">
            <Text size="3" weight="bold">Total due today</Text>
            <Text size="3" weight="bold">{formatCurrency(taxPreview.total ?? 0, taxPreview.currency || "gbp")}</Text>
          </Flex>
        </Flex>
      )}

      {taxError && (
        <Text size="2" color="red">
          Tax calculation error: {taxError?.message || "Unable to calculate tax"}
        </Text>
      )}
    </Flex>
  );
}

function PaymentMethodChoice({ onSelectCard, onSelectBacs }) {
  return (
    <Flex direction="column" gap="3">
      <Card>
        <Flex direction="column" gap="3">
          <Flex direction="column" gap="1">
            <Text size="3" weight="medium">Card</Text>
            <Text size="2" color="gray">
              Use a card and provide the billing contact and address details in this modal.
            </Text>
          </Flex>
          <Flex justify="end">
            <Button onClick={onSelectCard}>Use card</Button>
          </Flex>
        </Flex>
      </Card>

      <Card>
        <Flex direction="column" gap="3">
          <Flex direction="column" gap="1">
            <Text size="3" weight="medium">UK bank account</Text>
            <Text size="2" color="gray">
              Use Bacs Direct Debit and let Stripe collect the billing details inside the payment form.
            </Text>
          </Flex>
          <Flex justify="end">
            <Button onClick={onSelectBacs}>Use UK bank account</Button>
          </Flex>
        </Flex>
      </Card>
    </Flex>
  );
}

function SubscriptionPaymentForm({ paymentMethodType, country, payingCustomerId, taxPreview, onClose, onBack, onProcessingChange }) {
  const stripe = useStripe();
  const elements = useElements();
  const activateSubscription = useActivateSubscription();
  const confirmSubscription = useConfirmSubscription();
  const { setToastMessage } = useToast();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const setProcessingState = (value) => {
    setProcessing(value);
    onProcessingChange?.(value);
  };
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const requiresIdentity = paymentMethodType === "card";

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!country) {
      setError("Country is required");
      return;
    }

    if (!taxPreview) {
      setError("Tax preview is unavailable. Please try again.");
      return;
    }

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedAddress = address.trim();
    const trimmedCity = city.trim();
    const trimmedPostalCode = postalCode.trim();

    if (requiresIdentity) {
      if (!trimmedName) {
        setError("Name is required for card payments.");
        return;
      }

      if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
        setError("A valid email is required for card payments.");
        return;
      }

      if (!trimmedAddress) {
        setError("Address is required for card payments.");
        return;
      }

      if (!trimmedCity) {
        setError("City is required for card payments.");
        return;
      }

      if (!trimmedPostalCode) {
        setError("Postal code is required for card payments.");
        return;
      }
    }

    setProcessingState(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message);
        setProcessingState(false);
        return;
      }

      const activationPayload = {
        country,
        ...(payingCustomerId && { paying_customer_id: payingCustomerId }),
      };

      if (requiresIdentity) {
        activationPayload.name = trimmedName;
        activationPayload.email = trimmedEmail;
        activationPayload.postal_code = trimmedPostalCode;
        activationPayload.address = trimmedAddress;
        activationPayload.city = trimmedCity;
      }

      const activationResult = await activateSubscription.mutateAsync(activationPayload);

      const activationData = activationResult.data || activationResult;
      const paymentAlreadyHandled = ["succeeded", "processing"].includes(activationData.payment_intent_status);

      if (paymentAlreadyHandled || !activationData.payment_intent_client_secret) {
        setToastMessage(
          activationData.pending_payment
            ? "Payment is processing. Stripe should confirm the debit soon."
            : "Subscription activated successfully!",
        );
        onClose();
        return;
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: activationData.payment_intent_client_secret,
        confirmParams: {
          return_url: `${window.location.origin}/organisation/billing`,
          ...(requiresIdentity
            ? {
                payment_method_data: {
                  billing_details: {
                    name: trimmedName,
                    email: trimmedEmail,
                    address: {
                      country,
                      postal_code: trimmedPostalCode,
                      line1: trimmedAddress,
                      city: trimmedCity,
                    },
                  },
                },
              }
            : {}),
        },
        redirect: "if_required",
      });

      if (confirmError) {
        // setError(confirmError.message || "Unable to confirm payment");
        setError("Unable to confirm payment.");
        setProcessingState(false);
        return;
      }

      if (!paymentIntent || !["succeeded", "processing"].includes(paymentIntent.status)) {
        setError("Payment confirmation is incomplete. Please try again.");
        setProcessingState(false);
        return;
      }

      const confirmResult = await confirmSubscription.mutateAsync({
        subscription_id: activationData.subscription_id,
        customer_id: activationData.customer_id,
        ...(payingCustomerId && { paying_customer_id: payingCustomerId }),
      });

      const confirmationData = confirmResult.data || confirmResult;
      setToastMessage(
        confirmationData.pending_payment
          ? "Payment is processing. Stripe should confirm the debit soon."
          : "Subscription activated successfully!",
      );
      onClose();
    } catch (requestError) {
      // setError(requestError.response?.data?.error || requestError.message || "Failed to activate subscription");
      setError("Failed to activate subscription.");
    } finally {
      setProcessingState(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Flex direction="column" gap="4">
        {requiresIdentity && (
          <Flex direction="column" gap="2">
            <Text size="3" weight="bold">Billing Contact</Text>
            <Text size="2" color="gray">
              We store these details in Stripe because the card form doesn’t collect them automatically.
            </Text>
            <TextField.Root
              placeholder="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={processing}
            />
            <TextField.Root
              type="email"
              placeholder="Email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={processing}
            />
            <TextField.Root
              placeholder="Postal Code"
              value={postalCode}
              onChange={(event) => setPostalCode(event.target.value)}
              disabled={processing}
            />
            <TextField.Root
              placeholder="Address"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              disabled={processing}
            />
            <TextField.Root
              placeholder="City"
              value={city}
              onChange={(event) => setCity(event.target.value)}
              disabled={processing}
            />
          </Flex>
        )}

        <Flex direction="column" gap="2">
          <Text size="3" weight="bold">Payment Details</Text>
          <Text size="2" color="gray">
            {requiresIdentity
              ? "Stripe collects the card details here."
              : "Stripe collects the payment method and billing details here. Bacs Direct Debit payments can take several business days to settle."}
          </Text>
          <div
            style={{
              padding: "12px",
              border: "1px solid var(--gray-6)",
              borderRadius: "var(--radius-2)",
              backgroundColor: "var(--color-background)",
            }}
          >
            <PaymentElement
              options={{
                layout: "accordion",
                defaultValues: {
                  billingDetails: {
                    address: {
                      country: country || "GB",
                    },
                  },
                },
              }}
            />
          </div>
        </Flex>

        {error && <Text size="2" color="red">{error}</Text>}

        <Flex gap="3" justify="end">
          {onBack && (
            <Button type="button" variant="soft" color="gray" onClick={onBack} disabled={processing}>
              Back
            </Button>
          )}
          <Button type="button" variant="soft" color="gray" onClick={onClose} disabled={processing}>
            Cancel
          </Button>
          <Button type="submit" disabled={!stripe || processing} loading={processing}>
            Activate subscription
          </Button>
        </Flex>
      </Flex>
    </form>
  );
}

export function ActivateSubscriptionModal({ open, onOpenChange, products, payingCustomerId }) {
  const [country, setCountry] = useState("GB");
  const [activeFlow, setActiveFlow] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  const countriesList = useMemo(() => {
    const countryObj = countries.getNames("en", { select: "official" });
    return Object.entries(countryObj)
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const previewCountry = open ? country : null;
  const { data: taxPreview, isLoading: isTaxLoading, error: taxError } = useTaxPreview(
    previewCountry,
    "",
    payingCustomerId,
  );

  const currency = useMemo(() => {
    if (taxPreview?.currency) {
      return taxPreview.currency.toLowerCase();
    }

    if (products?.[0]?.product?.currency) {
      return products[0].product.currency.toLowerCase();
    }

    return "gbp";
  }, [products, taxPreview]);

  const amountInMinorUnits = useMemo(() => {
    const totalAmount = Number.parseFloat(taxPreview?.total ?? 0);
    if (!Number.isFinite(totalAmount) || totalAmount <= 0) {
      return null;
    }

    return Math.round(totalAmount * 100);
  }, [taxPreview]);

  const supportedPaymentMethodTypes = useMemo(() => {
    if (activeFlow === "bacs_debit" && currency === "gbp") {
      return ["bacs_debit"];
    }

    return ["card"];
  }, [activeFlow, currency]);

  const allowBacs = currency === "gbp";
  const currentFlow = allowBacs ? activeFlow : "card";
  const elementsKey = amountInMinorUnits && currentFlow
    ? `${currentFlow}-${country}-${currency}-${amountInMinorUnits}`
    : null;

  useEffect(() => {
    if (!open) {
      setCountry("GB");
      setActiveFlow(null);
      return;
    }

    if (!allowBacs) {
      setActiveFlow("card");
    }
  }, [open, allowBacs]);

  const handleClose = () => {
    if (paymentProcessing) return;
    setCountry("GB");
    setActiveFlow(null);
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={(nextOpen) => (nextOpen ? onOpenChange(true) : handleClose())}>
      <Dialog.Content
        style={{ maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
        onEscapeKeyDown={(event) => { if (paymentProcessing) event.preventDefault(); }}
        onPointerDownOutside={(event) => { if (paymentProcessing) event.preventDefault(); }}
        onInteractOutside={(event) => { if (paymentProcessing) event.preventDefault(); }}
      >
        <Flex justify="between" style={{ alignItems: "center", marginBottom: "var(--space-4)" }}>
          <Dialog.Title style={{ marginBottom: 0 }}>Activate subscription</Dialog.Title>
          {!paymentProcessing && (
            <Dialog.Close>
              <Button variant="ghost" color="gray" type="button">
                <Cross2Icon />
              </Button>
            </Dialog.Close>
          )}
        </Flex>

        <Dialog.Description size="2" color="gray" style={{ marginBottom: "var(--space-4)" }}>
          Select the billing country for tax, then choose whether to pay with a card or UK Bacs Direct Debit.
        </Dialog.Description>

        <div style={{ overflowY: "auto", overflowX: "hidden", flex: 1, paddingRight: "8px" }}>
          <Flex direction="column" gap="4">
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">Billing Country</Text>
              <Select.Root value={country} onValueChange={setCountry}>
                <Select.Trigger variant="surface" placeholder="Select country" style={{ width: "100%" }} />
                <Select.Content style={{ maxHeight: "300px" }} position="popper">
                  {countriesList.map((countryOption) => (
                    <Select.Item key={countryOption.code} value={countryOption.code}>
                      {countryOption.name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Flex>
            <Text size="1" color="gray">
              We use this country to calculate tax. Card billing details are collected in the form below.
            </Text>

            <OrderSummary
              products={products}
              taxPreview={taxPreview}
              isTaxLoading={isTaxLoading}
              taxError={taxError}
            />

            {!products?.length && (
              <Text size="2" color="red">
                No billable products are available for this subscription.
              </Text>
            )}

            {allowBacs && activeFlow === null && !isTaxLoading && !taxError && products?.length > 0 && (
              <PaymentMethodChoice
                onSelectCard={() => setActiveFlow("card")}
                onSelectBacs={() => setActiveFlow("bacs_debit")}
              />
            )}

            {isTaxLoading && (
              <Flex direction="column" gap="3" align="center" justify="center" py="4">
                <Spinner />
                <Text size="2" color="gray">Preparing secure payment form...</Text>
              </Flex>
            )}

            {!isTaxLoading && !taxError && products?.length > 0 && amountInMinorUnits && elementsKey && currentFlow && (
              <Elements
                key={elementsKey}
                stripe={stripePromise}
                options={{
                  mode: "payment",
                  amount: amountInMinorUnits,
                  currency,
                  setupFutureUsage: "off_session",
                  paymentMethodTypes: supportedPaymentMethodTypes,
                }}
              >
                <SubscriptionPaymentForm
                  paymentMethodType={currentFlow}
                  country={country}
                  payingCustomerId={payingCustomerId}
                  taxPreview={taxPreview}
                  onClose={handleClose}
                  onBack={allowBacs ? () => setActiveFlow(null) : null}
                  onProcessingChange={setPaymentProcessing}
                />
              </Elements>
            )}
          </Flex>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
